import { UserStatus } from './userStatusChecker';

/**
 * Flow Requirements Service
 *
 * Determines which flows are needed based on user status.
 * Maps status conditions to flows.
 * Implements business logic: "IF this condition is missing, THEN show this flow"
 */

export interface PendingFlow {
  flowId: string;
  flowName: string;
  reason: string;
  priority: number; // Lower number = show first
}

class FlowRequirements {
  /**
   * Determine which flows are pending based on user status
   * Returns flows in priority order (first pending = show to user)
   */
  getPendingFlows(status: UserStatus): PendingFlow[] {
    const pending: PendingFlow[] = [];

    console.log('[FlowRequirements] Checking pending flows...');
    console.log('[FlowRequirements] Roles setup:', status.rolesSetup);
    console.log('[FlowRequirements] Users invited:', status.usersInvited);

    // Rule 1: Roles must be created FIRST (dependency: other flows depend on this)
    if (!status.rolesSetup.completed) {
      console.log('[FlowRequirements] → Missing role setup, adding onboarding_flow');
      pending.push({
        flowId: 'onboarding_flow',
        flowName: 'Onboarding: Setup Roles & Invitations',
        reason: 'incomplete_role_setup',
        priority: 1,
      });
      return pending; // Stop here - other flows depend on roles being created first
    }

    // Rule 2: Then invite users (after roles are created)
    if (!status.usersInvited.completed) {
      console.log('[FlowRequirements] → Missing user invitations, adding onboarding_flow');
      pending.push({
        flowId: 'onboarding_flow',
        flowName: 'Onboarding: Setup Roles & Invitations',
        reason: 'incomplete_user_invitation',
        priority: 2,
      });
      return pending;
    }

    // Rule 3+: Add additional flows here as features grow
    // Example for future:
    // if (!status.esgDataFilled.completed) {
    //   pending.push({
    //     flowId: 'esg_questionnaire_flow',
    //     reason: 'missing_esg_data',
    //     priority: 3,
    //   });
    // }

    console.log('[FlowRequirements] → All setup complete, no pending flows');
    return pending;
  }

  /**
   * Get the NEXT pending flow (first one that hasn't been completed)
   */
  getNextFlow(status: UserStatus): PendingFlow | null {
    const pending = this.getPendingFlows(status);
    const next = pending[0] || null;

    if (next) {
      console.log('[FlowRequirements] Next pending flow:', next.flowId, `(reason: ${next.reason})`);
    } else {
      console.log('[FlowRequirements] No pending flows');
    }

    return next;
  }
}

export const flowRequirements = new FlowRequirements();
